// apps/platform/src/features/ProblemWorkspace/ProblemTopBar.tsx
import { useState } from 'react';
import { useRouter, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, List, ChevronLeft, ChevronRight, Shuffle, Play, Send, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UserMenu } from '@/components/UserMenu';
import { useProblem } from '@/lib/hooks/useProblems';
import { useProblemSequence } from '@/lib/hooks/useProblemSequence';
import { usePreviewStore } from '@/stores/previewStore';
import { cn } from '@/lib/utils';
import { ProblemListDrawer } from './ProblemListDrawer';

interface Props {
  slug: string;
  onRun: () => void;
  onSubmit: () => void;
  isJudging: boolean;
  isPromptMode?: boolean;
  /** Where the student came from. Drives what "next" means. */
  topicId?: string;
  batchId?: string;
}

const DIFFICULTY_STYLES: Record<string, string> = {
  easy: 'bg-green-50 text-green-700',
  medium: 'bg-amber-50 text-amber-700',
  hard: 'bg-red-50 text-red-700',
};

/**
 * The workspace's only chrome.
 *
 * `/problems/$slug` is deliberately exempt from AppLayout (see __root.tsx), so
 * whatever is not here does not exist on this page. That had two consequences
 * worth naming, both fixed here:
 *
 *  - The bar carried no identity at all: no title, no difficulty, no solved
 *    state. The browser tab was the only place the problem's name appeared.
 *  - The account menu went with the navbar, so Profile, Settings and Sign out
 *    were unreachable from this page entirely.
 *
 * Laid out as a three-column grid rather than flex-with-spacers so the centred
 * actions stay centred regardless of how long the problem title is. With
 * `margin:auto` the centre drifts as the left column grows, which is what made
 * this arrangement fragile before the account cluster gave it a right column.
 */
export function ProblemTopBar({ slug, onRun, onSubmit, isJudging, isPromptMode, topicId, batchId }: Props) {
  const router = useRouter();
  const navigate = useNavigate();
  const previewActive = usePreviewStore((s) => s.active);
  const [listOpen, setListOpen] = useState(false);

  const { data: problem } = useProblem(slug);
  const seq = useProblemSequence(slug, { topicId, batchId });

  const goto = (s?: string) => {
    if (s) navigate({ to: '/problems/$slug', params: { slug: s }, search: { topicId, batchId } });
  };

  const shuffle = () => {
    const pool = seq.entries.filter((p) => p.slug !== slug);
    if (pool.length === 0) return;
    goto(pool[Math.floor(Math.random() * pool.length)].slug);
  };

  const difficulty = problem?.difficulty?.toLowerCase();
  const solved = seq.entries[seq.index]?.solved;

  return (
    <>
      <div className="grid h-12 shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-border bg-card px-2 sm:px-3">
        {/* Left: navigation + identity */}
        <div className="flex min-w-0 items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => router.history.back()}
            aria-label="Go back"
            title="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 gap-1.5"
            onClick={() => setListOpen(true)}
            aria-label="Problem list"
          >
            <List className="h-4 w-4" />
            <span className="hidden lg:inline">Problem List</span>
          </Button>
          <div className="mx-1 hidden h-5 w-px shrink-0 bg-border sm:block" aria-hidden="true" />
          <Button
            variant="ghost"
            size="icon"
            className="hidden h-8 w-8 shrink-0 sm:inline-flex"
            onClick={() => goto(seq.prev?.slug)}
            disabled={!seq.prev}
            aria-label="Previous problem"
            title={seq.prev ? `Previous: ${seq.prev.title}` : 'No previous problem'}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {/* Only for a named set. "12 / 2539" against the whole catalogue is
              noise, not orientation. */}
          {seq.isScoped && seq.index >= 0 && (
            <span className="hidden shrink-0 tabular-nums text-xs text-muted-foreground sm:inline">
              {seq.index + 1} / {seq.entries.length}
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="hidden h-8 w-8 shrink-0 sm:inline-flex"
            onClick={() => goto(seq.next?.slug)}
            disabled={!seq.next}
            aria-label="Next problem"
            title={seq.next ? `Next: ${seq.next.title}` : 'No next problem'}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="hidden h-8 w-8 shrink-0 lg:inline-flex"
            onClick={shuffle}
            disabled={seq.entries.length === 0}
            aria-label="Random problem"
            title="Pick a random problem"
          >
            <Shuffle className="h-4 w-4" />
          </Button>

          <div className="mx-1 hidden h-5 w-px shrink-0 bg-border md:block" aria-hidden="true" />
          <span className="hidden truncate text-sm font-semibold text-foreground md:inline" title={problem?.title}>
            {problem?.title}
          </span>
          {difficulty && (
            <span
              className={cn(
                'ml-1.5 hidden shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize md:inline',
                DIFFICULTY_STYLES[difficulty] ?? 'bg-muted text-muted-foreground',
              )}
            >
              {difficulty}
            </span>
          )}
          {solved && (
            <span
              className="ml-1 hidden shrink-0 items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700 lg:inline-flex"
              title="You have solved this problem"
            >
              <CheckCircle2 className="h-3 w-3" />
              Solved
            </span>
          )}
        </div>

        {/* Centre: the actions */}
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={onRun}
            disabled={isJudging || previewActive}
            // The label is display:none below sm, which leaves the button with
            // no accessible name at all on a phone.
            aria-label={isPromptMode ? 'Try Prompt' : 'Run'}
            title={previewActive ? 'Not available in student preview' : undefined}
            className="h-8 gap-1.5"
          >
            <Play className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{isPromptMode ? 'Try Prompt' : 'Run'}</span>
          </Button>
          <Button
            size="sm"
            onClick={onSubmit}
            disabled={isJudging || previewActive}
            aria-label={isPromptMode ? 'Submit & Grade' : 'Submit'}
            title={previewActive ? 'Not available in student preview' : undefined}
            className="h-8 gap-1.5 bg-green-600 hover:bg-green-700 text-white"
          >
            {isPromptMode ? <Sparkles className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">{isPromptMode ? 'Submit & Grade' : 'Submit'}</span>
          </Button>
        </div>

        {/* Right: the account cluster. Without it there is no route to Profile,
            Settings or Sign out from this page — and nothing to balance the
            centred actions against. */}
        <div className="flex items-center justify-end">
          <UserMenu size={26} showName={false} className="px-1 py-1" />
        </div>
      </div>
      <ProblemListDrawer open={listOpen} onClose={() => setListOpen(false)} currentSlug={slug} />
    </>
  );
}
